/**
 * TaskOutcomePicker (TASK-922) — the four things that can have happened to a signal.
 *
 * `PATCH /api/tasks/{id}/complete` has required an outcome for any task carrying
 * `subject_key` since TASK-724, and no frontend call site ever sent one: completing
 * Radar-raised work from Today answered 400 `outcome_required`, and not a single completion
 * in the database carries an outcome. This is the missing half.
 *
 * Why a picker rather than a default. The server's whole argument is that completion alone
 * never resolves a condition — a drift breach is true or false as a matter of portfolio
 * state, not because someone ticked a box. Defaulting to `implemented_change` would put a
 * claim about the portfolio in the advisor's mouth; defaulting to
 * `investigation_complete_no_action` would record "looked at it" over work that was actually
 * done. Neither is recoverable from the audit trail afterwards, so the advisor is asked.
 *
 * Each option states its EFFECT, because the counter-intuitive part is that none of them
 * closes the signal. An advisor who picks "I made the change" and then watches the signal
 * stay on the Radar has to be told, at the moment of choosing, that the next sweep is what
 * clears it — otherwise the honest behaviour reads as a bug.
 *
 * Used by TaskCard (compact and full) and TasksAwaitingReview, so the sign-off path and the
 * Do-column path cannot drift apart.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  TASK_OUTCOMES,
  TASK_OUTCOME_COPY,
  type Task,
  type TaskOutcome,
} from '../../types/tasks';

export interface TaskOutcomeSubmission {
  outcome: TaskOutcome;
  outcomeRationale?: string;
  snoozedUntil?: string;
}

interface Props {
  task: Task;
  onConfirm: (submission: TaskOutcomeSubmission) => void;
  onCancel: () => void;
  busy?: boolean;
}

/**
 * `datetime-local` wants LOCAL `YYYY-MM-DDTHH:mm`; the server wants ISO. Default: a week out,
 * on the hour.
 *
 * The first version rounded in local time and then formatted with `toISOString()`, which is
 * UTC — so the prefilled "Come back on" read two hours early in Zurich. Confirm was always
 * correct (an offset-less string parses as local), so it was display only, which is exactly
 * how a timezone bug survives. Formatted from the local parts instead.
 */
function defaultDeferral(): string {
  const at = new Date(Date.now() + 7 * 24 * 3600 * 1000);
  at.setMinutes(0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `T${pad(at.getHours())}:${pad(at.getMinutes())}`
  );
}

export function TaskOutcomePicker({ task, onConfirm, onCancel, busy = false }: Props) {
  const [outcome, setOutcome] = useState<TaskOutcome | null>(null);
  const [rationale, setRationale] = useState('');
  const [snoozedUntil, setSnoozedUntil] = useState(defaultDeferral);

  // `deferred` is the one outcome that reschedules the task, so it is the one that needs a
  // date. Empty means the server would complete it with no return — silently losing the work.
  const deferralMissing = outcome === 'deferred' && !snoozedUntil;
  const canConfirm = outcome !== null && !deferralMissing && !busy;

  return (
    <div
      data-testid="task-outcome-picker"
      className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-xs"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="mb-1.5 font-medium text-gray-700">
        What happened?
        <span className="ml-1 font-normal text-gray-500">
          This task came from a signal, so closing it has to say.
        </span>
      </p>

      <div className="space-y-1">
        {TASK_OUTCOMES.map((value) => (
          <label
            key={value}
            className={`flex cursor-pointer gap-2 rounded px-1.5 py-1 ${
              outcome === value ? 'bg-white ring-1 ring-[#002B45]' : 'hover:bg-white'
            }`}
          >
            <input
              type="radio"
              name={`outcome-${task.id}`}
              value={value}
              checked={outcome === value}
              onChange={() => setOutcome(value)}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="block font-medium text-gray-800">
                {TASK_OUTCOME_COPY[value].label}
              </span>
              <span className="block text-[11px] text-gray-500">
                {TASK_OUTCOME_COPY[value].effect}
              </span>
            </span>
          </label>
        ))}
      </div>

      {outcome === 'deferred' && (
        <label className="mt-2 block">
          <span className="text-gray-600">Come back on</span>
          <input
            type="datetime-local"
            data-testid="outcome-snoozed-until"
            value={snoozedUntil}
            onChange={(e) => setSnoozedUntil(e.target.value)}
            className="mt-0.5 w-full rounded border border-gray-300 px-1.5 py-1"
          />
        </label>
      )}

      <label className="mt-2 block">
        <span className="text-gray-600">Why (optional)</span>
        <textarea
          data-testid="outcome-rationale"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={2}
          placeholder="What you did, or why this is the right call"
          className="mt-0.5 w-full rounded border border-gray-300 px-1.5 py-1"
        />
      </label>

      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="outcome-confirm"
          disabled={!canConfirm}
          onClick={() =>
            outcome &&
            onConfirm({
              outcome,
              outcomeRationale: rationale.trim() || undefined,
              snoozedUntil: outcome === 'deferred'
                ? new Date(snoozedUntil).toISOString()
                : undefined,
            })
          }
          className="inline-flex items-center gap-1 rounded bg-[#002B45] px-2 py-1 font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          Complete
        </button>
      </div>
    </div>
  );
}

export default TaskOutcomePicker;
